/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC} from 'react'
import { useIntl } from 'react-intl'
import classNames from 'classnames'
import { CriticalityLevel, ITicketDto } from '../Ticket.model'
import { parseDate } from '../../../../util/Locale'
import { IconLabelContent } from '../../../../common/component/IconLabelContent'

interface Props {
  disabled?: boolean
  data: ITicketDto
  rowSelected?: string
  backgroundClass?: string
  hoverBackgroundClass?: string
  onRowClick?: (id: string) => void
  removeOnClick?: (id: string) => void
}

export const TicketListItem: FC<Props> = ({
  disabled,
  data,
  rowSelected,
  backgroundClass = 'bg-light-primary',
  hoverBackgroundClass = 'bg-hover-light-primary',
  onRowClick,
  removeOnClick,
}: Props) => {
  const intl = useIntl()
  const backGroundClasses = {}
  backGroundClasses[`d-flex flex-stack py-2 ${backgroundClass}`] = data.id === rowSelected

  const hoverBackGroundClasses = {}
  hoverBackGroundClasses[`d-flex flex-stack py-2 ${hoverBackgroundClass}`] = data.id !== rowSelected

  const buildDescriptionLine = (data: ITicketDto) => {
    let description = ``
    if (data.statusCode) {
      description += description
        ? ` | ${intl.formatMessage({id: `DROPDOWN.TICKET.STATUS.${data.statusCode}`})}`
        : `| ${intl.formatMessage({id: `DROPDOWN.TICKET.STATUS.${data.statusCode}`})}`
    }
    return description
  }

  const buildAssetDescriptionLine = (data: ITicketDto) => {
    let description = ``
    if (data.asset) {
      description += description
        ? ` | ${data.asset.family} | ${data.asset.area} | ${data.asset.brand} | ${data.asset.model} | ${data.asset.serial}`
        : `${data.asset.family} | ${data.asset.area} | ${data.asset.brand} | ${data.asset.model} | ${data.asset.serial}`
    }

    return description
  }

  const buildClientDescriptionLine = (data: ITicketDto) => {
    let description = ``
    if (data.client) {
      description += description ? ` | ${data.client.displayName}` : `${data.client.displayName}`
    }
    if (data.reportedBy) {
      description += description ? ` | ${data.reportedBy.email}` : `${data.reportedBy.email}`
    }
    return description
  }

  return (
    <div
      className={classNames(backGroundClasses, hoverBackGroundClasses)}
      onClick={() => onRowClick && onRowClick(data.id)}
    >
      <div className='d-flex flex-stack'>
        <div className='d-flex align-items-center'>
          {!disabled && removeOnClick && (
            <div className='row'>
              <div className='d-flex justify-content-end align-items-center'>
                <button
                  type='button'
                  className='btn btn-sm btn-icon'
                  onClick={() => removeOnClick(data.id)}
                >
                  <i className='bi bi-x fs-1 fw-bold text-danger'></i>
                </button>
              </div>
            </div>
          )}

          <div className='ms-2'>
            <div className='fs-7'>
              <span className='text-primary mb-2'>
                {`${parseDate(data.createdAt)}`}
              </span>
              <span className='text-gray-800 ms-2 me-2 mb-2'>-</span>
              <span className='fw-bolder mb-2'>{`${data.title} `}</span>
              <span className='text-gray-600 ms-2 me-2 mb-2'>-</span>
              <span className='text-primary mb-2'>{data.sequence}</span>
            </div>

            <div className='d-flex justify-content-start'>
              <div className='d-flex align-items-center fs-8'>
                <span
                  className={classNames('me-1', {
                    'text-danger': data.criticalityCode === CriticalityLevel.SEVERE,
                    'text-warning': data.criticalityCode === CriticalityLevel.HIGH,
                    'text-success': data.criticalityCode === CriticalityLevel.MODERATE,
                    'text-primary': data.criticalityCode === CriticalityLevel.LOW,
                  })}
                >
                  {intl.formatMessage({id: `TICKET.CRITICALITY.${data.criticalityCode}`})}
                </span>
                <span className='text-gray-600'>{buildDescriptionLine(data)}</span>
              </div>
            </div>

            <div className='d-flex justify-content-start'>
              <div className='d-flex align-items-center fs-8'>
                <span className='text-gray-600'>{buildClientDescriptionLine(data)}</span>
              </div>
            </div>

            <div className='d-flex justify-content-start'>
              <div className='d-flex align-items-center fs-8'>
                <span className='text-gray-600'>{buildAssetDescriptionLine(data)}</span>
              </div>
            </div>

            <div className='d-flex justify-content-start align-items-center'>
              <IconLabelContent
                title={
                  (data.totalAttachments ?? 0) > 0
                    ? intl
                        .formatMessage({
                          id:
                            data.totalAttachments === 1
                              ? 'TITLE.ATTACHMENT.TOTAL_SINGLE'
                              : 'TITLE.ATTACHMENT.TOTAL_MULTIPLE',
                        })
                        .replace(/%s/g, `${data.totalAttachments}`)
                    : intl.formatMessage({id: 'TITLE.ATTACHMENT.ZERO'})
                }
                active={(data.totalAttachments ?? 0) > 0}
                labelValue={`${data.totalAttachments || 0}`}
                icon='bi bi-paperclip'
              />

              <IconLabelContent
                title={
                  (data.totalTasks ?? 0) > 0
                    ? intl
                        .formatMessage({
                          id:
                            data.totalTasks === 1
                              ? 'TITLE.TASK.TOTAL_SINGLE'
                              : 'TITLE.TASK.TOTAL_MULTIPLE',
                        })
                        .replace(/%s/g, `${data.totalTasks}`)
                    : intl.formatMessage({id: 'TITLE.TASK.ZERO'})
                }
                active={(data?.totalTasks ?? 0) > 0}
                labelValue={`${data.totalTasks || 0}`}
                icon='bi bi-stickies'
              />

              {/* <IconLabelContent
                  title={`${intl.formatMessage({
                    id: 'UTIL.CRITICALITY_LEVEL',
                  })}: ${intl.formatMessage({
                    id: `TICKET.CRITICALITY.${data.criticality}`,
                  })}`}
                  active={true}
                  icon={'bi bi-exclamation-circle'}
                  activeColor={
                    data.criticality === CriticalityLevel.SEVERE
                      ? 'text-danger'
                      : data.criticality === CriticalityLevel.HIGH
                      ? 'text-warning'
                      : data.criticality === CriticalityLevel.MODERATE
                      ? 'text-success'
                      : 'text-primary'
                  }
                /> */}

              {/* {data.status !== TicketStatus.NEW && (
                  <IconLabelContent
                    title={intl.formatMessage({id: `DROPDOWN.TASK.STATUS.${data.status}`})}
                    active={true}
                    icon={(() => {
                      switch (data.status) {
                        case TicketStatus.IN_PROGRESS:
                          return 'bi bi-bug text-primary'
                        case TicketStatus.ON_HOLD:
                          return 'bi bi-clock text-danger'
                        case TicketStatus.RESOLVED:
                          return 'bi bi-check2 text-primary'
                        case TicketStatus.CLOSED:
                          return 'bi bi-check2-all text-primary'
                        default:
                          return 'bi bi-question-circle text-gray-500'
                      }
                    })()}
                  />
                )} */}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
